/**
 * @fileoverview VisualDX main entry: runs all feature initializers on DOMContentLoaded.
 * Order matters for some (e.g. cmsNest before other scripts when nest links exist). Failures are isolated per module.
 */

import { checkUnrecognisedVdxAttributes, logError } from './errors.js';
import { handleReadTimeOnPageLoad, processReadTimeLink } from './readtime.js';
import { socialShare } from './socialshare.js';
import { formatDate } from './date.js';
import { currentTime } from './time.js';
import { onClick } from './onclick.js';
import { copyToClipboard } from './copyclip.js';
import { popupsIntent } from './popup.js';
import { cmsNest } from './cmsnest.js';
import { backButtons } from './backbutton.js';
import { generateTOC } from './toc.js';
import { transitionDelay } from './transitiondelay.js';
import { showNavScroll } from './shownavscroll.js';
import { initializeCountdowns } from './countdowntimer.js';
import { initializeToastButtons } from './toast.js';
import { countUp } from './countup.js';
import { customTabs, timedTabs } from './tabs.js';
import { handleChildHoverState } from './hoverstate.js';
import { generateTooltip } from './tooltip.js';
import { cmsRandomSort } from './cmsrandomsort.js';
import { cmsMerge } from './cmsmerge.js';
import { initSlider } from './slider.js';
import { removeElements } from './removeelement.js';
import { filterItems } from './filters.js';
import { generateFootnotes } from './footnotes.js';
import { mirrorClick } from './mirrorclick.js';
import { addSmoothScroll } from './scrolloffset.js';
import { accordian } from './accordian.js';
import { urlParams } from './urlparams.js';
import { togglePasswordVisibility } from './hideshowpassword.js';
import { horizontalScroll } from './GSAPHorizontalScroll.js';
import { staggerIn } from './GSAPStagger.js';
import { typewriter } from './GSAPTypewriter.js';
import { maskedText } from './GSAPMaskedText.js';
import { redirectURL } from './redirectURL.js';
import { revealHiddenText } from './GSAPRevealHiddenText.js';
import { openDropdown } from './opendropdown.js';
import { initBreadcrumbs } from './breadcrumbs.js';

const MODULE = 'functions';
const NEST_LINK_SELECTOR = '[vdx-nest-link]';
const READTIME_LINK_SELECTOR = '[vdx-readtime-link]';

/**
 * Runs a single initializer; logs on error and does not rethrow so the rest can run.
 * @param {string} name - Label for logging.
 * @param {() => void} fn - Initializer to run.
 */
function runSafe(name, fn) {
    try {
        fn();
    } catch (err) {
        logError(`Initializer "${name}" threw.`, { module: MODULE, error: err });
    }
}

/**
 * Main entry: runs attribute check, then cmsNest (when nest links exist), then all other initializers.
 * Each initializer runs in try/catch so one failure does not stop the rest.
 */
export function functions() {
    if (typeof document === 'undefined') return;

    runSafe('checkUnrecognisedVdxAttributes', checkUnrecognisedVdxAttributes);

    const nestLinks = document.querySelectorAll(NEST_LINK_SELECTOR);
    if (nestLinks.length > 0) {
        runSafe('cmsNest', cmsNest);
    }
    runSafe('cmsRandomSort', cmsRandomSort);
    runSafe('cmsMerge', cmsMerge);

    document.querySelectorAll(READTIME_LINK_SELECTOR).forEach((el) => {
        runSafe('processReadTimeLink', () => processReadTimeLink(el));
    });

    const initializers = [
        ['handleReadTimeOnPageLoad', handleReadTimeOnPageLoad],
        ['socialShare', socialShare],
        ['formatDate', formatDate],
        ['currentTime', currentTime],
        ['onClick', onClick],
        ['copyToClipboard', copyToClipboard],
        ['popupsIntent', popupsIntent],
        ['backButtons', backButtons],
        ['generateTOC', generateTOC],
        ['transitionDelay', transitionDelay],
        ['showNavScroll', showNavScroll],
        ['initializeCountdowns', initializeCountdowns],
        ['initializeToastButtons', initializeToastButtons],
        ['countUp', countUp],
        ['customTabs', customTabs],
        ['timedTabs', timedTabs],
        ['handleChildHoverState', handleChildHoverState],
        ['generateTooltip', generateTooltip],
        ['removeElements', removeElements],
        ['initSlider', initSlider],
        ['generateFootnotes', generateFootnotes],
        ['mirrorClick', mirrorClick],
        ['filterItems', filterItems],
        ['addSmoothScroll', addSmoothScroll],
        ['accordian', accordian],
        ['urlParams', urlParams],
        ['togglePasswordVisibility', togglePasswordVisibility],
        ['horizontalScroll', horizontalScroll],
        ['staggerIn', staggerIn],
        ['typewriter', typewriter],
        ['maskedText', maskedText],
        ['revealHiddenText', revealHiddenText],
        ['redirectURL', redirectURL],
        ['openDropdown', openDropdown],
        ['initBreadcrumbs', initBreadcrumbs],
    ];

    initializers.forEach(([name, fn]) => {
        try {
            fn();
        } catch (err) {
            logError(`Initializer "${name}" threw.`, { module: MODULE, error: err });
        }
    });
}
